import {put, call, all} from 'redux-saga/effects';
import Court from "../../entities/court";
import * as Actions from "../actions/courtActions";
import * as idb from '../../lib/idbd';
import * as api from '../../rest/api';
import {processQueue} from "../../queues/processQueue";
import newHRTimestamp from "../../lib/picos/timestamp";
import {ensureOperationId} from '../../lib/pendingRows';
import {tabSession} from '../../Auth/session';

// Inlined to reduce circular dependencies
function getCurrentUserId() {
    return sessionStorage.getItem('userId');
}

// Inlined to reduce circular dependencies
function getCurrentFirmId() {
    return sessionStorage.getItem('firmId');
}

type CourtAction = {
    courtId: string;
    court: Court;
    courts: Court[];
    sentAddedOn?: number;
    onQueued?: (saved: boolean) => void;
};

export function* fetchOneCourt(action: CourtAction) {
	const session = tabSession.captureGeneration();
	try {
		const court: Court = yield call(api.findOne, 'court', action.courtId);
		session.assertCurrent();

		// Now we can update the user interface
		yield put({type: Actions.COURT_FETCH_ONE_SUCCESS, courtId: action.courtId, court, sentAddedOn: action.sentAddedOn});
	}
	catch(err) {
		if (session.signal.aborted) return;
		yield put({type: Actions.COURT_FETCH_ONE_FAILURE, courtId: action.courtId, status: 500, err});

		console.error('Error fetching from database', err);
	}
}

/**
 * @param {CourtAction} action
 */
export function* saveCourt(action: CourtAction) {
    const session = tabSession.captureGeneration();
    let queueReported = false;
    const reportQueued = (saved: boolean) => {
        if (!queueReported) { queueReported = true; action.onQueued?.(saved); }
    };
    try {
        // console.debug('⮂ COURT_SAVE saga', action.court?.id);

		if (action.court === undefined || action.court === null) {
			console.error('saveCourt received a null');
			throw new Error('saveCourt received a null');
		}

		if (!(action.court instanceof Court)) {
			action.court = new Court(action.court);
		}
		// OWA-1018: the reducer showed this save at dispatch, stamped after the version this tab holds
		// (OWA-1083), marked pending under this queue key
		const dispatchedAddedOn = action.court.addedOn;

		try {
			// Save to IndexedDB
			ensureOperationId(action.court);
			const addResult = yield call(idb.add, 'court', action.court);
			if (addResult.isErr()) {
				console.error(`⮂ saveCourt: IDB write failed: ${addResult.unwrapErr().message}`);
				reportQueued(false);
				session.assertCurrent();
				// The save never reached the queue: its row returns to the latest accepted version
				yield put(Actions.revertCourtSave(action.court, {sentAddedOn: dispatchedAddedOn}));
				yield put({type: Actions.COURT_SAVE_FAILURE, court: action.court, status: 500, message: addResult.unwrapErr().message});
				return;
			}
			session.assertCurrent();
			reportQueued(true);

			// Now we can update the user interface: the queued copy replaces the row shown at dispatch
			// (the queue may have stamped it again)
			yield put({type: Actions.COURT_SAVE, court: action.court, dispatchedAddedOn, queuedAt: performance.now()});

			// A save never deletes autosaved drafts (OWA-958). The editor that wrote a draft
			// removes it when it saves; a draft typed somewhere else may be newer than this copy.
			session.assertCurrent();

			// Let the queue know to process
			yield call(processQueue);
		}
		catch(err) {
			if (session.signal.aborted) { reportQueued(false); return; }
			// OWA-1018: a save that never reached the queue returns its row to the latest accepted version
			if (!queueReported) yield put(Actions.revertCourtSave(action.court, {sentAddedOn: dispatchedAddedOn}));
			reportQueued(false);
			yield put({type: Actions.COURT_SAVE_FAILURE, court: action.court, status: 500, err});

			console.error('Error saving to database', err);
		}
    } finally { reportQueued(false); }
}

/**
 * TODO: Update for saveMany
 * @param {CourtAction} action
 * @param {Court[]} action.courts
 */
export function* saveManyCourts(action: CourtAction) {
    const session = tabSession.captureGeneration();
    let queueReported = false;
    const reportQueued = (saved: boolean) => {
        if (!queueReported) { queueReported = true; action.onQueued?.(saved); }
    };
    try {
        // console.debug('⮂ COURT_SAVE_MANY saga', action.courts.map(court => court.id).join(','));

        if (!Array.isArray(action.courts)) {
            console.error('saveManyCourts expected an array, received', action.courts);
            throw new Error('saveManyCourts expected an array');
        }
		// OWA-1018: the reducer showed these saves at dispatch (see saveCourt), under these queue keys
		const dispatchedAddedOns: number[] = action.courts.map(court => court?.addedOn);
		const revertAll = () => action.courts.map((court, i) =>
			put(Actions.revertCourtSave(court, {sentAddedOn: dispatchedAddedOns[i]})));

		try {
			const toSave: Court[] = [];

			// Save each item to IndexedDB
			for(let i = 0; i < action.courts.length; i++) {
				const court = action.courts[i];
				court.persisted = false;
				ensureOperationId(court);
				toSave.push(court);
			}

			const addResult = yield call(idb.add, 'court', toSave);
			if (addResult.isErr()) {
				console.error(`⮂ saveManyCourts: IDB write failed: ${addResult.unwrapErr().message}`);
				reportQueued(false);
				session.assertCurrent();
				// The saves never reached the queue: their rows return to the latest accepted versions
				yield all(revertAll());
				yield put({type: Actions.COURT_SAVE_MANY_FAILURE, courts: action.courts, status: 500, message: addResult.unwrapErr().message});
				return;
			}
			session.assertCurrent();
			reportQueued(true);

			// Now we can update the user interface: the queued copies replace the rows shown at dispatch
			yield put({type: Actions.COURT_SAVE_MANY, courts: toSave, dispatchedAddedOns, queuedAt: performance.now()});
			session.assertCurrent();

			// Let the queue know to process
			yield call(processQueue)
		}
		catch(err) {
			if (session.signal.aborted) { reportQueued(false); return; }
			// OWA-1018: saves that never reached the queue return their rows to the latest accepted versions
			if (!queueReported) yield all(revertAll());
			reportQueued(false);
			yield put({type: Actions.COURT_SAVE_MANY_FAILURE, courts: action.courts, status: 500, err});

			console.error(`Error saving ${action.courts.length} courts to database`, err);
		}
    } finally { reportQueued(false); }
}

/**
 * @param {CourtAction} action
 */
export function* removeCourt(action: CourtAction) {
    const session = tabSession.captureGeneration();
    const userId = getCurrentUserId();
    let queueReported = false;
    const reportQueued = (saved: boolean) => {
        if (!queueReported) { queueReported = true; action.onQueued?.(saved); }
    };
	if (!userId || action.court === undefined || action.court === null || !(action.court instanceof Court)) {
			console.error('removeCourt expected type Court, received', action.court);
			reportQueued(false);
			throw new Error('removeCourt expected type Court');
		}

	try {
		// Mark as deleted. The stamp follows the version this tab holds (the reducer raised the clock, OWA-1083).
		action.court.status = 'D';
		action.court.addedOn = newHRTimestamp();
		action.court.addedBy = userId;

		// Save to IndexedDB
		ensureOperationId(action.court);
		const addResult = yield call(idb.add, 'court', action.court);
		session.assertCurrent();
		if (addResult.isErr()) {
			console.error(`⮂ removeCourt: IDB write failed: ${addResult.unwrapErr().message}`);
			reportQueued(false);
			yield put({type: Actions.COURT_REMOVE_FAILURE, court: action.court, status: 500, message: addResult.unwrapErr().message});
			return;
		}

		// Now we can update the user interface
		yield put({type: Actions.COURT_REMOVE, court: action.court});
		session.assertCurrent();

        reportQueued(true);

		// Let the queue know to process
		yield call(processQueue)
	}
	catch(err) {
		if (session.signal.aborted) return;
		yield put({type: Actions.COURT_REMOVE_FAILURE, court: action.court, status: 500, err});

		console.error('Error deleting from database', err);
	}
    finally { reportQueued(false); }
}

/**
	* Restore the last version of this Court.
	*
	* @param {CourtAction} action
	*/
export function* restoreCourt(action: CourtAction) {
    const session = tabSession.captureGeneration();
    const userId = getCurrentUserId();
    if (!userId) {
        console.error('restoreCourt expected getCurrentUserId() to return a value');
        return;
    }

	if (action.court === undefined || action.court === null) {
        console.error('restoreCourt expected a Court, received null');
        throw new Error('restoreCourt expected a Court, received null');
    }
    else if (!(action.court instanceof Court)) action.court = new Court(action.court); 

	try {
		// Mark as restored. The stamp follows the version this tab holds (the reducer raised the clock, OWA-1083).
		action.court.status = 'U';
		action.court.addedOn = newHRTimestamp();
		action.court.addedBy = userId;
		action.court.restore = true;

		// Save to IndexedDB
		ensureOperationId(action.court);
		const addResult = yield call(idb.add, 'court', action.court);
		session.assertCurrent();
		if (addResult.isErr()) {
			console.error(`⮂ restoreCourt: IDB write failed: ${addResult.unwrapErr().message}`);
			yield put({type: Actions.COURT_RESTORE_FAILURE, court: action.court, status: 500, message: addResult.unwrapErr().message});
			return;
		}

		// Now we can update the user interface
		yield put({type: Actions.COURT_RESTORE, court: action.court});
	}
	catch(err) {
		if (session.signal.aborted) return;
		yield put({type: Actions.COURT_RESTORE_FAILURE, court: action.court, status: 500, err});

		console.error('Error restoring from database', err);
	}
}